import JobZImage from "../../../common/jobz-img";
import { NavLink, useLocation } from "react-router-dom";
import { loadScript, setMenuActive } from "../../../../globals/constants";
import { employer, empRoute, publicUser } from "../../../../globals/route-names";
import { useEffect, useRef, useState } from "react";
import { api } from "../../../../utils/api";
import "./emp-sidebar-scroll-fix.css";

function EmpSidebarSection({ sidebarActive, isMobile, onClose }) {
    const currentpath = useLocation().pathname;
    const [hasNewTickets, setHasNewTickets] = useState(false);
    const [unreadNotifications, setUnreadNotifications] = useState(0);
    const [scrollThumbHeight, setScrollThumbHeight] = useState(48);
    const [scrollThumbTop, setScrollThumbTop] = useState(0);
    const [isScrolling, setIsScrolling] = useState(false);
    const navRef = useRef(null);
    const scrollTimerRef = useRef(null);

    const checkNewTickets = async () => {
        try {
            const token = localStorage.getItem('employerToken');
            if (!token) return;
            
            const data = await api.getEmployerSupportTickets({ status: 'new', limit: 1 });
            
            if (data.success) {
                setHasNewTickets(data.unreadCount > 0 || (data.tickets && data.tickets.some(t => !t.isRead)));
            }
        } catch (error) {
            console.error('Error checking new support tickets:', error);
        }
    };

    const checkNotificationCount = async () => {
        try {
            const token = localStorage.getItem('employerToken');
            if (!token) {
                setUnreadNotifications(0);
                return;
            }

            const response = await fetch('http://localhost:5000/api/notifications/employer?page=1&limit=1', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setUnreadNotifications(Number(data.unreadCount ?? 0));
                }
            }
        } catch (error) {
            console.error('Error checking employer notifications:', error);
        }
    };

    useEffect(() => {
        loadScript("js/custom.js");
        loadScript("js/emp-sidebar.js");
        checkNewTickets();
        checkNotificationCount();

        const interval = setInterval(() => {
            checkNewTickets();
            checkNotificationCount();
        }, 45000);

        const handleNotificationRefresh = () => {
            checkNotificationCount();
        };

        window.addEventListener('refreshNotifications', handleNotificationRefresh);

        return () => {
            clearInterval(interval);
            window.removeEventListener('refreshNotifications', handleNotificationRefresh);
        };
    }, []);

    useEffect(() => {
        const navElement = navRef.current;

        if (!navElement) {
            return undefined;
        }

        const updateScrollRail = () => {
            const { clientHeight, scrollHeight, scrollTop } = navElement;
            const hasOverflow = scrollHeight > clientHeight + 1;

            if (!hasOverflow) {
                setScrollThumbHeight(Math.max(clientHeight - 20, 48));
                setScrollThumbTop(0);
                return;
            }

            const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, 36);
            const maxThumbTop = clientHeight - thumbHeight;
            const maxScrollTop = scrollHeight - clientHeight;
            const thumbTop = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * maxThumbTop : 0;

            setScrollThumbHeight(thumbHeight);
            setScrollThumbTop(thumbTop);
        };

        const handleScroll = () => {
            updateScrollRail();
            setIsScrolling(true);
            if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
            scrollTimerRef.current = setTimeout(() => setIsScrolling(false), 800);
        };

        let frameId = 0;
        const scheduleUpdate = () => {
            if (frameId) {
                cancelAnimationFrame(frameId);
            }

            frameId = requestAnimationFrame(() => {
                frameId = 0;
                updateScrollRail();
            });
        };

        scheduleUpdate();
        window.setTimeout(scheduleUpdate, 0);
        window.setTimeout(scheduleUpdate, 150);

        navElement.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", scheduleUpdate);

        let resizeObserver;
        if (typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(scheduleUpdate);
            resizeObserver.observe(navElement);
            if (navElement.firstElementChild) {
                resizeObserver.observe(navElement.firstElementChild);
            }
        }

        const mutationObserver = new MutationObserver(scheduleUpdate);
        mutationObserver.observe(navElement, { childList: true, subtree: true });

        return () => {
            if (frameId) {
                cancelAnimationFrame(frameId);
            }
            navElement.removeEventListener("scroll", handleScroll);
            if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
            window.removeEventListener("resize", scheduleUpdate);
            resizeObserver?.disconnect();
            mutationObserver.disconnect();
        };
    }, [sidebarActive, isMobile]);

    const handleLinkClick = () => {
        if (isMobile && onClose) {
            onClose();
        }
    };

    const sidebarClasses = [
        "emp-sidebar",
        sidebarActive ? "active" : "",
        !isMobile && !sidebarActive ? "collapsed" : ""
    ].filter(Boolean).join(" ");

    return (
        <>
            <nav 
                id="sidebar-admin-wraper" 
                className={sidebarClasses}
                style={isMobile ? {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "min(280px, 84vw)",
                    transform: sidebarActive ? "translateX(0)" : "translateX(-100%)",
                    transition: "transform 0.3s ease",
                    boxShadow: sidebarActive ? "0 4px 24px rgba(0,0,0,0.25)" : "none",
                    zIndex: 10000,
                    background: "#ffffff",
                    height: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "hidden"
                } : {}}>
                <div className="page-logo">
                    <NavLink to={publicUser.INITIAL}><JobZImage id="skin_page_logo" src="images/logo-dark.png" alt="" /></NavLink>
                </div>

                <div className="emp-sidebar-nav-shell">
                    <div ref={navRef} className="admin-nav scrollbar-macosx">
                        <ul>
                        <li
                            className={setMenuActive(currentpath, empRoute(employer.DASHBOARD))}>
                            <NavLink to={empRoute(employer.DASHBOARD)} onClick={handleLinkClick} style={{display: 'flex', alignItems: 'center'}}><i className="fa fa-home" style={{minWidth: '30px', textAlign: 'center'}} /><span className="admin-nav-text" style={{paddingLeft: '10px'}}>Dashboard</span></NavLink>
                        </li>

                        <li className={setMenuActive(currentpath, empRoute(employer.NOTIFICATIONS))}>
                            <NavLink to={empRoute(employer.NOTIFICATIONS)} onClick={handleLinkClick} style={{display: 'flex', alignItems: 'center'}}>
                                <i className="fa fa-bell" style={{minWidth: '30px', textAlign: 'center'}} />
                                <span className="admin-nav-text" style={{paddingLeft: '10px'}}>Notifications</span>
                                {unreadNotifications > 0 && (
                                    <span className="admin-nav-badge">
                                        {unreadNotifications > 99 ? '99+' : unreadNotifications}
                                    </span>
                                )}
                            </NavLink>
                        </li>

                        <li
                            className={setMenuActive(currentpath, empRoute(employer.PROFILE))}>
                            <NavLink to={empRoute(employer.PROFILE)} onClick={handleLinkClick} style={{display: 'flex', alignItems: 'center'}}><i className="fa fa-user-tie" style={{minWidth: '30px', textAlign: 'center'}} /><span className="admin-nav-text" style={{paddingLeft: '10px'}}>Company Profile</span></NavLink>
                        </li>

                        <li
                            className={setMenuActive(currentpath, empRoute(employer.POST_A_JOB))}>
                            <NavLink to={empRoute(employer.POST_A_JOB)} onClick={handleLinkClick} style={{display: 'flex', alignItems: 'center'}}><i className="fa fa-plus-circle" style={{minWidth: '30px', textAlign: 'center'}} /><span className="admin-nav-text" style={{paddingLeft: '10px'}}>Post Job</span></NavLink>
                        </li>

                        <li
                            className={setMenuActive(currentpath, empRoute(employer.MANAGE_JOBS))}>
                            <NavLink to={empRoute(employer.MANAGE_JOBS)} onClick={handleLinkClick} style={{display: 'flex', alignItems: 'center'}}><i className="fa fa-suitcase" style={{minWidth: '30px', textAlign: 'center'}} /><span className="admin-nav-text" style={{paddingLeft: '10px'}}>Manage Interview</span></NavLink>
                        </li>

                        <li className={setMenuActive(currentpath, empRoute(employer.CREATE_ASSESSMENT))}>
                            <NavLink to={empRoute(employer.CREATE_ASSESSMENT)} onClick={handleLinkClick} style={{display: 'flex', alignItems: 'center'}}><i className="fa fa-clipboard-check" style={{minWidth: '30px', textAlign: 'center'}} /><span className="admin-nav-text" style={{paddingLeft: '10px'}}>Assessments</span></NavLink>
                        </li>

                        <li className={setMenuActive(currentpath, empRoute(employer.CANDIDATES))}>
                            <NavLink to={empRoute(employer.CANDIDATES)} onClick={handleLinkClick} style={{display: 'flex', alignItems: 'center'}}><i className="fa fa-user-friends" style={{minWidth: '30px', textAlign: 'center'}} /><span className="admin-nav-text" style={{paddingLeft: '15px'}}>Applicants</span></NavLink>
                        </li>

                        <li className={setMenuActive(currentpath, empRoute(employer.SUPPORT_TICKETS))}>
                            <NavLink to={empRoute(employer.SUPPORT_TICKETS)} onClick={handleLinkClick} style={{display: 'flex', alignItems: 'center'}}>
                                <i className="fa fa-ticket-alt" style={{minWidth: '30px', textAlign: 'center'}} />
                                <span className="admin-nav-text" style={{paddingLeft: '10px'}}>Candidate Tickets</span>
                                {hasNewTickets && (
                                    <span style={{
                                        display: 'inline-block',
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: '#ff4444',
                                        borderRadius: '50%',
                                        marginLeft: '8px'
                                    }}></span>
                                )}
                            </NavLink>
                        </li>

                        <li className={setMenuActive(currentpath, empRoute(employer.SUPPORT))}>
                            <NavLink to={empRoute(employer.SUPPORT)} onClick={handleLinkClick} style={{display: 'flex', alignItems: 'center'}}><i className="fa fa-headset" style={{minWidth: '30px', textAlign: 'center'}} /><span className="admin-nav-text" style={{paddingLeft: '10px'}}>Support</span></NavLink>
                        </li>
                        
                        <li>
                            <a href="#" data-bs-toggle="modal" data-bs-target="#logout-dash-profile" onClick={handleLinkClick} style={{display: 'flex', alignItems: 'center'}}>
                                <i className="fa fa-share-square" style={{minWidth: '30px', textAlign: 'center'}} />
                                <span className="admin-nav-text" style={{paddingLeft: '10px'}}>Logout</span>
                            </a>
                        </li>
                        </ul>
                    </div>
                    <div className="emp-sidebar-scroll-rail" aria-hidden="true">
                        <div
                    className={`emp-sidebar-scroll-thumb${isScrolling ? ' is-scrolling' : ''}`}
                            style={{
                                height: `${scrollThumbHeight}px`,
                                transform: `translateY(${scrollThumbTop}px)`
                            }}
                        />
                    </div>
                </div>
            </nav>
        </>
    )
}

export default EmpSidebarSection;
